import { useState } from "react";
import { NavLink, Navigate, Outlet, Link, useNavigate, useSearchParams } from "react-router-dom";
import { LayoutDashboard, Map, MapPin, CalendarDays, Ticket, Inbox, Building2, Users, FileText, Search as SearchIcon, ShieldCheck, BarChart3, ScrollText, LogOut, Menu, X, Eye, EyeOff, Loader2, MessageSquareQuote, HelpCircle, Newspaper, LogIn } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import { useSeo } from "../../lib/seo";

const NAV = [
  ["/admin", "Dashboard", LayoutDashboard, "dashboard"],
  ["/admin/experiences", "Experiences", Map, "content"],
  ["/admin/destinations", "Destinations", MapPin, "content"],
  ["/admin/departures", "Departures", CalendarDays, "bookings"],
  ["/admin/bookings", "Bookings", Ticket, "bookings"],
  ["/admin/enquiries", "Enquiries", Inbox, "bookings"],
  ["/admin/corporate", "Corporate leads", Building2, "bookings"],
  ["/admin/customers", "Customers", Users, "bookings"],
  ["/admin/posts", "Blog", Newspaper, "content"],
  ["/admin/content", "Website content", FileText, "content"],
  ["/admin/testimonials", "Testimonials", MessageSquareQuote, "content"],
  ["/admin/faqs", "FAQs", HelpCircle, "content"],
  ["/admin/seo", "SEO", SearchIcon, "content"],
  ["/admin/reports", "Reports", BarChart3, "reports"],
  ["/admin/users", "Users & roles", ShieldCheck, "users"],
  ["/admin/logs", "Logs & integrations", ScrollText, "settings"],
];

export function AdminLayout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useSeo({ title: "Admin", robots: "noindex,nofollow" }, []);
  if (user === null) return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const side = (
    <nav className="flex flex-col gap-0.5 p-3" aria-label="Admin">
      {NAV.filter((n) => can(n[3])).map(([to, l, I]) => (
        <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white text-black font-semibold" : "text-white/75 hover:bg-[#222] hover:text-white"}`}>
          <I className="h-4 w-4" />{l}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="min-h-screen bg-[#F5F5F5] lg:flex" data-testid="admin-layout">
      <aside className={`${open ? "fixed inset-0 z-50" : "hidden"} lg:sticky lg:top-0 lg:block lg:h-screen w-full lg:w-64 shrink-0 bg-black text-white on-dark overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-[#222]">
          <Link to="/" className="flex items-center gap-2"><img src="/logo.png" alt="Bhatkya भटक्या" className="h-10 w-auto logo-invert" /><span className="text-[10px] uppercase tracking-[0.2em] text-white/60">Admin</span></Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-5 w-5" /></button>
        </div>
        {side}
        <div className="p-5 border-t border-[#222] text-xs text-white/60 space-y-3">
          <p data-testid="admin-user-email">{user.email}</p>
          <p className="font-mono uppercase tracking-wider">{user.role.replace("_", " ")}</p>
          <button className="btn-ghost-light btn-sm w-full" onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout"><LogOut className="h-3 w-3" />Log out</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between bg-black text-white px-5 h-14"><span className="font-display font-bold">Bhatkya Admin</span><button onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-mobile-menu"><Menu className="h-5 w-5" /></button></div>
        <main className="bg-white min-h-screen"><Outlet /></main>
      </div>
    </div>
  );
}

export function RequirePerm({ perm, children }) {
  const { can } = useAuth();
  return can(perm) ? children : <div className="p-10" data-testid="no-permission"><h1 className="font-display text-3xl font-bold">No access</h1><p className="text-[#666] mt-2">Your role does not have permission to view this section.</p></div>;
}

const AuthShell = ({ title, sub, children }) => {
  useSeo({ title, robots: "noindex,nofollow" }, [title]);
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-black" data-testid="auth-shell">
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden text-white on-dark">
        <img src="/images/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/40 to-black/70" />
        <div className="relative p-12"><Link to="/" aria-label="Bhatkya home"><img src="/logo.png" alt="Bhatkya भटक्या" className="h-14 w-auto logo-invert" /></Link></div>
        <div className="relative p-12 max-w-lg space-y-6">
          <p className="eyebrow !text-white/60">Admin panel</p>
          <p className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]">Every trail, booking and story — managed in one place.</p>
          <p className="text-white/70 text-sm leading-relaxed">Experiences, departures, bookings, enquiries, content and the blog. Sign in with your team account.</p>
        </div>
        <div className="relative p-12 pt-0 text-xs text-white/50">© {new Date().getFullYear()} Bhatkya · भटक्या</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="lg:hidden inline-block mb-10" aria-label="Bhatkya home"><img src="/logo.png" alt="Bhatkya भटक्या" className="h-12 w-auto" /></Link>
          <p className="eyebrow mb-3">Bhatkya admin</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mb-2">{title}</h1>
          {sub && <p className="text-sm text-[#666] mb-8">{sub}</p>}
          {children}
          <p className="mt-10 text-xs text-[#666]">Need access? Ask a Super Admin to create your account under Users &amp; roles.</p>
        </div>
      </div>
    </div>
  );
};

export function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (user) return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(f.email, f.password); nav("/admin"); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Welcome back" sub="Sign in to manage your adventures.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form" noValidate>
        <div><label className="field-label" htmlFor="l-email">Email</label><input id="l-email" type="email" required autoComplete="username" placeholder="you@company.com" className="field !py-3" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email" /></div>
        <div>
          <div className="flex items-center justify-between mb-1.5"><label className="field-label !mb-0" htmlFor="l-pw">Password</label><Link to="/admin/forgot-password" className="text-xs text-[#666] hover:text-black underline underline-offset-4" data-testid="forgot-password-link">Forgot password?</Link></div>
          <div className="relative"><input id="l-pw" type={show ? "text" : "password"} required autoComplete="current-password" placeholder="••••••••••" className="field !py-3 pr-11" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password" />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-black" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} data-testid="toggle-password">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div>
        </div>
        {err && <p className="border border-black bg-[#F5F5F5] p-3 text-sm" role="alert" data-testid="login-error">{err}</p>}
        <button className="btn-primary w-full !py-3.5" disabled={busy} data-testid="login-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}Sign in</button>
        <Link to="/" className="block text-center text-xs text-[#666] hover:text-black">← Back to website</Link>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e) => { e.preventDefault(); try { const r = await api.post("/auth/forgot-password", { email }); setMsg(r.data.message); } catch (x) { setMsg(errMsg(x)); } };
  return (
    <AuthShell title="Reset password">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div><label className="field-label" htmlFor="fp-email">Email</label><input id="fp-email" type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></div>
        {msg && <p className="border border-black p-3 text-sm" role="status" data-testid="forgot-message">{msg} (Email is in development mode — the reset link is recorded in server logs / notification log.)</p>}
        <button className="btn-primary w-full" data-testid="forgot-submit">Send reset link</button>
        <Link to="/admin/login" className="block text-sm underline">Back to sign in</Link>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); setMsg("Password updated. You can sign in now."); } catch (x) { setMsg(errMsg(x)); } };
  return (
    <AuthShell title="New password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div><label className="field-label" htmlFor="rp">New password (min 10 characters)</label><input id="rp" type="password" minLength={10} required className="field" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" /></div>
        {msg && <p className="border border-black p-3 text-sm" role="status">{msg}</p>}
        <button className="btn-primary w-full" data-testid="reset-submit">Update password</button>
        <Link to="/admin/login" className="block text-sm underline">Sign in</Link>
      </form>
    </AuthShell>
  );
}
